import React, { useState, useEffect, useMemo } from 'react';

import { Tooltip, ActionIcon } from '@mantine/core';
import { IconPrinter } from '@tabler/icons-react';
import { AxiosResponse } from 'axios';
import { ExportToCsv, Options } from 'export-to-csv';
import { useTracking } from 'hooks/mixpanel/useTracking';
import { UseQueryResult } from 'react-query';

interface DownloadNonPaginatedDataCSVProps<T> {
  useList: (params: any) => UseQueryResult<AxiosResponse<T[]>>;
  dataToRow: (data: T) => { [key: string]: any };
  csvOptions?: Options;
  enabled?: boolean;
  hookOptions?: { [key: string]: any };
  className?: string;
  reportType: string;
  startDate?: string | null;
  endDate?: string | null;
}

const DownloadNonPaginatedDataCSV = <T,>({
  useList,
  dataToRow,
  csvOptions,
  enabled = true,
  hookOptions,
  className,
  reportType,
  startDate,
  endDate,
}: DownloadNonPaginatedDataCSVProps<T>): JSX.Element => {
  const csvExporter = useMemo(() => new ExportToCsv(csvOptions), [csvOptions]);
  const tracking = useTracking();
  const [buttonClicked, setButtonClicked] = useState<boolean>(false);
  const { data, isSuccess, isLoading } = useList({
    enabled: buttonClicked && enabled,
    csvExport: 'csv-export',
    ...hookOptions,
  });

  useEffect(() => {
    if (buttonClicked && isSuccess) {
      if (data.data.length) {
        csvExporter.generateCsv(data.data.map(dataToRow));
        tracking.trackReportGenerated(reportType, startDate ?? null, endDate ?? null);
      }
      setButtonClicked(false);
    }
  }, [
    isSuccess,
    buttonClicked,
    data?.data,
    csvExporter,
    dataToRow,
    data,
    reportType,
    startDate,
    endDate,
    tracking,
  ]);

  return (
    <Tooltip label='Export data as CSV' className={className}>
      <ActionIcon onClick={() => setButtonClicked(true)} loading={buttonClicked && isLoading}>
        <IconPrinter />
      </ActionIcon>
    </Tooltip>
  );
};

export default DownloadNonPaginatedDataCSV;
